import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTenant } from '../../contexts/TenantContext';

const LoadingButton = ({ isLoading, children, disabled, ...props }: any) => (
  <Button
    {...props}
    disabled={disabled || isLoading}
    fullWidth={true}
    sx={{
      position: 'relative',
      textTransform: 'none',
      minWidth: 180,
      ...props.sx,
    }}
  >
    {isLoading && (
      <CircularProgress
        size={20}
        sx={{
          position: 'absolute',
          left: '50%',
          marginLeft: -10,
        }}
      />
    )}
    <span style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
      {children}
    </span>
  </Button>
);

interface ActionItemProps {
  title: string;
  description: string;
  onClick: () => Promise<void>;
  buttonVariant?: 'contained' | 'outlined';
  buttonColor?: 'primary' | 'error' | 'inherit';
  disabled?: boolean;
  buttonSx?: any;
}

const ActionItem: React.FC<ActionItemProps> = ({
  title,
  description,
  onClick,
  disabled = false,
  buttonVariant = 'outlined',
  buttonColor = 'primary',
  buttonSx,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      await onClick();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <LoadingButton
        variant={buttonVariant}
        color={buttonColor}
        onClick={handleClick}
        isLoading={isLoading}
        disabled={disabled}
        sx={buttonSx}
      >
        {title}
      </LoadingButton>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: 'block' }}
      >
        {description}
      </Typography>
    </Box>
  );
};

const BackHandlerContent: React.FC<{
  onClose: () => void;
  publish: () => void;
  hasErrors: boolean;
  isMediaPending?: boolean;
  saveListing: () => void;
  discardListing: () => void;
  back: () => void;
}> = ({
  onClose,
  publish,
  saveListing,
  discardListing,
  hasErrors,
  isMediaPending,
  back,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAddEditHost } = useTenant();

  const isSaasTenant = isAddEditHost();

  // SaaS tenants have 3 columns (no discard), non-SaaS have 4 columns
  const gridMdSize = isSaasTenant ? 4 : 3;

  return (
    <Box sx={{}}>
      <Typography variant="body1" gutterBottom>
        What do you want to do with the changes?
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={isMobile ? 2 : 4}>
          <Grid
            size={{
              xs: 12,
              md: gridMdSize,
            }}
          >
            <ActionItem
              title="Publish Changes"
              description={
                hasErrors
                  ? 'There are some required fields missing from your listing, please add these before publishing.'
                  : isMediaPending
                    ? 'Media uploads or processing in progress. Please wait before publishing.'
                    : 'This will make your changes available to all search sites.'
              }
              disabled={hasErrors || isMediaPending}
              onClick={async () => {
                onClose();

                await publish();
              }}
              buttonVariant="contained"
              buttonSx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: gridMdSize,
            }}
          >
            <ActionItem
              title={isSaasTenant ? 'Save and Exit' : 'Save as 1-hour Draft'}
              description={
                isSaasTenant
                  ? 'Temporarily save your changes while you perform other tasks.'
                  : 'Temporarily save your changes for one hour while you perform other tasks. The listing will be unavailable to others during this time.'
              }
              onClick={async () => {
                await saveListing();

                back();

                onClose();
              }}
            />
          </Grid>
          {!isSaasTenant && (
            <Grid
              size={{
                xs: 12,
                md: 3,
              }}
            >
              <ActionItem
                title="Discard Changes"
                description="Discard your changes to unlock the listing and revert to the last published version."
                onClick={async () => {
                  await discardListing();

                  back();
                }}
                buttonColor="error"
              />
            </Grid>
          )}
          <Grid
            size={{
              xs: 12,
              md: gridMdSize,
            }}
          >
            <ActionItem
              title="Cancel"
              description="Return to the edit form to make additional edits to the listing."
              onClick={async () => {
                onClose();
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BackHandlerContent;
